import { useEffect, useRef, useState } from "react";
import { IoClose } from "react-icons/io5";
import { LuArrowUp } from "react-icons/lu";
import { useIsCompactView } from "../../../core_stores/useIsCompactViewStore";
import { useProjectActions, useProjectStore } from "../../../core_stores/useProjectStore";
import {
  useSubscriptionActions,
  useUserSubscription,
} from "../../../core_stores/useSubscriptionStore";
import { LOCAL_STORAGE_KEYS } from "../../../config/local_storage_keys";
import { colors, components, typography } from "../../../design/tokens";
import { useFileContextTrigger } from "../../../hooks/useFileContextTrigger";
import * as SharedMessageTypes from "../../../../darvin-shared/schema/zod/agent/message-types.schema";
import {
  HttpAssistantIsWorkingException,
  HttpNotEnoughTokensException,
} from "../../../model/httpException";
import { useGradientBuilderModal } from "../../../providers/GradientBuilderModalProvider";
import { usePromptLibraryModal } from "../../../providers/PromptLibraryModalProvider";
import { useSnackbar } from "../../../providers/SnackbarProvider";
import { useSubscriptionsModal } from "../../../providers/SubscriptionsModalProvider";
import { formatNumberWithSpaces, formatTokensCompact } from "../../../utils/numberFormatter";
import { useChatMessagesActions } from "../../../view_stores/chat_messages/useChatMessagesStore";
import { useIsProjectProcessing } from "../../../view_stores/useProjectStatusStore";
import {
  ALLOWED_IMAGE_TYPES,
  useAttachedImage,
  useMaxPromptLength,
  usePrompt,
  usePromptInputActions,
} from "../../../view_stores/usePromptInputStore";
import {
  useCanHandleTechnicalError,
  useTechnicalErrorHandlerActions,
} from "../../../view_stores/useTechnicalErrorHandlerStore";
import { AttachedImageIcon } from "../../common_components/AttachedImageIcon";
import { AttachmentsBtn } from "../../common_components/AttachmentsBtn";
import { FileContextPicker } from "../../common_components/FileContextPicker";
import { ModelSelectorBtn } from "./ModelSelectorBtn";

export function ProjectChatTextField() {
  const maxPromptLength = useMaxPromptLength();
  const prompt = usePrompt();
  const attachedImage = useAttachedImage();
  const canHandleTechnicalError = useCanHandleTechnicalError();
  const subscription = useUserSubscription();
  const { open: openSubscriptionsModal } = useSubscriptionsModal();

  const {
    setPrompt,
    setAttachedImage,
    addFileReference,
    isLengthExceeded: isPromptLengthExceeded,
    loadFromLocalStorage: loadPromptFromLocalStorage,
    clear: clearPromptInput,
  } = usePromptInputActions();
  const { addTemporaryMessage, removeTemporaryMessages } =
    useChatMessagesActions();
  const { sendMessage } = useProjectActions();
  const { rollback } = useTechnicalErrorHandlerActions();
  const { showSnackbar } = useSnackbar();
  const { open: openPromptLibrary } = usePromptLibraryModal();
  const { open: openGradientBuilder } = useGradientBuilderModal();
  const { reloadSubscriptionsInfo } = useSubscriptionActions();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const lastCursorPositionRef = useRef<number>(0);
  const [isPromptSubmitting, setIsPromptSubmitting] = useState(false);
  // Read initial dismissal state from localStorage for persistence across sessions
  const [isLowTokenWarningDismissed, setIsLowTokenWarningDismissed] = useState(
    () => localStorage.getItem(LOCAL_STORAGE_KEYS.LOW_TOKEN_WARNING_DISMISSED) === "true"
  );

  // Show low token warning when balance < 100K and not dismissed
  // Round to integer first to treat fractional tokens as zero
  const showLowTokenWarning = subscription && Math.round(subscription.remaining) < 100000 && Math.round(subscription.remaining) > 0 && !isLowTokenWarningDismissed;

  const isProcessing = useIsProjectProcessing();
  const isCompactView = useIsCompactView();

  // File context trigger (@filepath:) integration
  const fileContext = useFileContextTrigger(prompt, setPrompt, textareaRef, addFileReference);

  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    // Reset height to auto to get the correct scrollHeight
    textarea.style.height = 'auto';

    // Calculate new height
    const newHeight = textarea.scrollHeight;

    // Set max height to ~40% of viewport
    const maxHeight = window.innerHeight * 0.4;

    // Apply height with constraints
    textarea.style.height = `${Math.min(newHeight, maxHeight)}px`;
  };

  useEffect(() => {
    loadPromptFromLocalStorage();
  }, []);

  useEffect(() => {
    adjustTextareaHeight();
  }, [prompt]);

  useEffect(() => {
    if (isProcessing) {
      setIsPromptSubmitting(false);
    }
    // NOT_ENOUGH_TOKENS case removed - prompt is never cleared when this error occurs
    // because sendMessage() throws before clearPromptInput() is called
  }, [isProcessing]);

  // Clear dismissal state when tokens go back above 100K (so warning can reappear next time)
  useEffect(() => {
    if (subscription && subscription.remaining >= 100000) {
      localStorage.removeItem(LOCAL_STORAGE_KEYS.LOW_TOKEN_WARNING_DISMISSED);
      setIsLowTokenWarningDismissed(false);
    }
  }, [subscription]);

  // Periodically refresh subscription info to keep token balance up-to-date
  // This ensures the low token warning and token counter stay accurate
  useEffect(() => {
    // Refresh every 30 seconds while user is in project view
    const refreshInterval = setInterval(() => {
      reloadSubscriptionsInfo();
    }, 30000);

    return () => clearInterval(refreshInterval);
  }, [reloadSubscriptionsInfo]);

  // Refresh subscription when AI finishes processing (jobDone event)
  // Ensures token balance updates immediately when job completes
  useEffect(() => {
    const unsubscribe = useProjectStore.subscribe(
      (state) => state.latestMsg,
      (latestMsg) => {
        if (latestMsg?.type === SharedMessageTypes.MessageTypeEnum.enum.jobDone) {
          // Job completed - refresh token balance to show accurate count
          reloadSubscriptionsInfo();
        }
      }
    );

    return () => unsubscribe();
  }, [reloadSubscriptionsInfo]);

  const canSubmit =
    !isProcessing && prompt?.trim() && !isPromptLengthExceeded();

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (ALLOWED_IMAGE_TYPES.includes(file.type)) {
      setAttachedImage(file);
    } else {
      showSnackbar(
        `Unsupported file type. Please select JPEG, PNG, or WebP images.`,
        { type: "error" }
      );
    }
  };

  const handleRemoveImage = () => {
    setAttachedImage(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handlePaste = async (e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;

    if (!items) return;

    for (const item of items) {
      if (item.type.startsWith("image/")) {
        const file = item.getAsFile();
        if (file && ALLOWED_IMAGE_TYPES.includes(file.type)) {
          e.preventDefault();
          setAttachedImage(file);
          break;
        }
      }
    }
  };

  const handleCursorPositionChange = () => {
    if (!textareaRef.current || !prompt) return;

    const cursorPos = textareaRef.current.selectionStart;
    const selectionEnd = textareaRef.current.selectionEnd;

    // Only snap cursor when there's no text selection
    if (cursorPos !== selectionEnd) {
      lastCursorPositionRef.current = cursorPos;
      return;
    }

    // Determine movement direction
    const lastPos = lastCursorPositionRef.current;
    const movingRight = cursorPos > lastPos;
    const movingLeft = cursorPos < lastPos;

    // Find all @file: tokens and check if cursor is inside any of them
    const tokenRegex = /@file:[^\s]+/g;
    let match;
    while ((match = tokenRegex.exec(prompt)) !== null) {
      const tokenStart = match.index;
      const tokenEnd = match.index + match[0].length;

      // Check if cursor is inside this token (between start and end, exclusive of boundaries)
      if (cursorPos > tokenStart && cursorPos < tokenEnd) {
        // Cursor is INSIDE token - apply directional snapping
        let snapPosition: number;
        if (movingRight) {
          // Moving right → snap to END of token
          snapPosition = tokenEnd;
        } else if (movingLeft) {
          // Moving left → snap to BEGINNING of token
          snapPosition = tokenStart;
        } else {
          // Mouse click or unknown → snap to END (default)
          snapPosition = tokenEnd;
        }

        // Apply snap
        textareaRef.current.setSelectionRange(snapPosition, snapPosition);
        lastCursorPositionRef.current = snapPosition;
        return;
      }
    }

    // Update last position for next comparison
    lastCursorPositionRef.current = cursorPos;
  };

  const handleKeyDown = async (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Smart backspace for @file: tokens
    if (e.key === "Backspace") {
      const cursorPos = textareaRef.current?.selectionStart ?? 0;
      const selectionEnd = textareaRef.current?.selectionEnd ?? 0;

      // Only apply smart backspace when there's no text selection
      if (cursorPos === selectionEnd && prompt) {
        // Strategy: Find if cursor is inside or at the end of any @file: token

        // Case 1: Check if cursor is at END of token
        const textBefore = prompt.slice(0, cursorPos);
        const matchAtEnd = textBefore.match(/@file:[^\s]+$/);

        if (matchAtEnd) {
          // Cursor at end of token - delete entire token
          e.preventDefault();
          const tokenStart = cursorPos - matchAtEnd[0].length;
          const deleteFrom = tokenStart > 0 && textBefore[tokenStart - 1] === ' '
            ? tokenStart - 1
            : tokenStart;

          const newText = prompt.slice(0, deleteFrom) + prompt.slice(cursorPos);
          setPrompt(newText);

          setTimeout(() => {
            textareaRef.current?.setSelectionRange(deleteFrom, deleteFrom);
          }, 0);
          return;
        }

        // Case 2: Check if cursor is INSIDE a token
        // Look backwards for @file: start
        const beforeCursor = prompt.slice(0, cursorPos);
        const afterCursor = prompt.slice(cursorPos);

        const fileStartMatch = beforeCursor.match(/@file:[^\s]*$/);
        if (fileStartMatch) {
          // Found @file: before cursor, check if rest of token continues after cursor
          const afterMatch = afterCursor.match(/^[^\s]*/);
          if (afterMatch && afterMatch[0].length > 0) {
            // Cursor is INSIDE token (e.g., "@file:lib/te|st.dart")
            // Delete entire token
            e.preventDefault();

            const tokenStart = cursorPos - fileStartMatch[0].length;
            const tokenEnd = cursorPos + afterMatch[0].length;

            // Also delete preceding space if exists
            const deleteFrom = tokenStart > 0 && prompt[tokenStart - 1] === ' '
              ? tokenStart - 1
              : tokenStart;

            const newText = prompt.slice(0, deleteFrom) + prompt.slice(tokenEnd);
            setPrompt(newText);

            setTimeout(() => {
              textareaRef.current?.setSelectionRange(deleteFrom, deleteFrom);
            }, 0);
            return;
          }
        }
      }
    }

    if (e.key === "Enter" && !e.shiftKey) {
      // On mobile: allow newline, don't send
      if (isCompactView) {
        return; // Let default behavior happen (newline)
      }

      // On desktop: send message (existing behavior)
      e.preventDefault();
      submit();
    }
  };

  const submit = async () => {
    if (canSubmit) {
      const promptToSend = prompt!.trim();

      // Parse inline file references from text for validation
      const fileReferenceRegex = /@file:([^\s]+)/g;
      const matches = [...promptToSend.matchAll(fileReferenceRegex)];
      const parsedReferencedFiles = matches.map(match => match[1]); // Extract file paths

      // Validation: max 10 files
      if (parsedReferencedFiles.length > 10) {
        showSnackbar("Maximum 10 files allowed per message", { type: "error" });
        return;
      }

      // File references are already inline in the message as @file:path
      // No need to append anything - send as-is

      setIsPromptSubmitting(true);
      try {
        if (canHandleTechnicalError) {
          await rollback(promptToSend, attachedImage);
          clearPromptInput();

          // Reset textarea height after sending
          if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
          }
        } else {
          await sendMessage(promptToSend, attachedImage);

          await addTemporaryMessage(promptToSend, attachedImage);
          clearPromptInput();

          // Reset textarea height after sending
          if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
          }

          // Reload subscription to update token balance in the warning banner
          reloadSubscriptionsInfo();

          setIsPromptSubmitting(false);
          if (fileInputRef.current) {
            fileInputRef.current.value = "";
          }
        }
      } catch (error) {
        if (error instanceof HttpAssistantIsWorkingException) {
          removeTemporaryMessages();
          setPrompt(prompt!.trim());
          setAttachedImage(attachedImage);
          setIsPromptSubmitting(false);
          showSnackbar(
            "Previous action is still in progress, try again later.",
            {
              type: "error",
              duration: 5000,
            }
          );
        } else if (error instanceof HttpNotEnoughTokensException) {
          // Message was not sent - prompt never cleared, no temp message added
          // Just reset submitting state - error message handled by store subscription
          setIsPromptSubmitting(false);
        }
      }
    }
  };

  return (
    <div className="relative h-auto">
      {/* File context picker */}
      <FileContextPicker
        isOpen={fileContext.isOpen}
        onClose={fileContext.onClose}
        onSelect={fileContext.onFileSelect}
        searchQuery={fileContext.searchQuery}
        dropdownPosition={fileContext.dropdownPosition}
        textareaWidth={textareaRef.current?.offsetWidth}
      />

      <div
        className="bg-dark-surface-secondary
      rounded-xl p-2 h-full
      border border-dark-border
      flex flex-col
      shadow-sm hover:shadow-md transition-shadow duration-200
      overflow-visible"
      >
        {/* Low token warning - integrated tab */}
        {showLowTokenWarning && (
          <div className="flex items-center justify-between px-2 py-1.5 mb-1 bg-dark-surface-tertiary rounded-lg">
            <span className="text-sm text-dark-text-secondary">
              {formatTokensCompact(subscription.remaining)}
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={openSubscriptionsModal}
                className={components.button.primaryCompact}
              >
                Add tokens
              </button>
              <button
                onClick={() => {
                  localStorage.setItem(LOCAL_STORAGE_KEYS.LOW_TOKEN_WARNING_DISMISSED, "true");
                  setIsLowTokenWarningDismissed(true);
                }}
                className="text-dark-text-secondary hover:text-dark-text transition-colors p-0.5"
              >
                <IoClose size={16} />
              </button>
            </div>
          </div>
        )}

        {/* Attached image preview - above text input */}
        {attachedImage && (
          <div className="px-2 pt-2">
            <AttachedImageIcon
              attachedImg={attachedImage}
              onRemove={handleRemoveImage}
            />
          </div>
        )}

        <textarea
          ref={textareaRef}
          className={`w-full bg-transparent resize-none focus:outline-none
          ${colors.text.primary} ${typography.size.sm}
          placeholder:text-dark-placeholder
          py-2 px-3`}
          style={{
            minHeight: '44px',
            maxHeight: '40vh',
            overflow: 'auto',
          }}
          value={prompt ?? ""}
          placeholder="What would you like to change?"
          onChange={(e) => {
            setPrompt(e.target.value);
            adjustTextareaHeight();
            fileContext.onTextareaChange(e);
          }}
          onKeyDown={(e) => {
            handleKeyDown(e);
            fileContext.onTextareaKeyDown(e);
          }}
          onKeyUp={handleCursorPositionChange}
          onClick={handleCursorPositionChange}
          onSelect={handleCursorPositionChange}
          onPaste={handlePaste}
          enterKeyHint="send"
        />
        <div className="flex justify-between items-center mt-2 gap-2">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <AttachmentsBtn
              className="flex-shrink-0 w-8 h-8 bg-dark-surface-tertiary
              hover:bg-dark-hover
              text-dark-text-muted hover:text-dark-text rounded-full transition-colors duration-200"
              onAddImageClick={handleUploadClick}
              onPromptLibraryClick={openPromptLibrary}
              onReferenceFilesClick={fileContext.openPicker}
              onGradientBuilderClick={openGradientBuilder}
              iconSize={18}
              dropdownPosition="top"
            />
            <ModelSelectorBtn
              className="flex-shrink-0 bg-dark-surface-tertiary
              hover:bg-dark-hover
              text-dark-text-muted hover:text-dark-text rounded-full px-2.5 py-1.5 transition-colors duration-200"
              isDisabled={isPromptSubmitting || isProcessing}
            />
          </div>
          <SubmitBtn
            isSubmitting={isPromptSubmitting}
            isDisabled={
              isPromptLengthExceeded() ||
              isPromptSubmitting ||
              isProcessing ||
              !prompt
            }
            onClick={submit}
          />
        </div>
        <input
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={handleImageUpload}
          className="hidden"
          ref={fileInputRef}
        />
      </div>
      {isPromptLengthExceeded() && (
        <div className="text-red-500 text-sm px-2 mt-1">
          Message is too long — max {formatNumberWithSpaces(maxPromptLength)}{" "}
          characters allowed.
        </div>
      )}
    </div>
  );
}

function SubmitBtn({
  isSubmitting,
  isDisabled,
  onClick,
}: {
  isSubmitting: boolean;
  isDisabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      id="project-chat-text-field-send"
      className={`flex-shrink-0 w-8 h-8 flex items-center justify-center
        bg-primary-blue text-white rounded-full
        hover:opacity-90 transition-opacity duration-300
        disabled:opacity-50 disabled:cursor-not-allowed`}
      onClick={onClick}
      disabled={isDisabled}
    >
      {isSubmitting ? (
        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
      ) : (
        <LuArrowUp className="w-5 h-5" />
      )}
    </button>
  );
}
